<template>
  <div style="position: relative">
    <div
      class="opc-plan-card"
      :id="'opc-plancard_' + plan.id"
      :class="{
        'opc-card-is-popular': isPopular,
        'opc-card-is-common': !isPopular,
        'opc-card-is-middle': isMiddle,
        'opc-card-is-current': isCurrent,
      }"
    >
      <div v-show="isPopular" class="opc-plan-popular-label">{{ $t('billing.common.most_popular') }}</div>
      <div v-if="planIcons.length" class="opc-plan-card__icon">
        <img 
          v-for="(iconName, index) in planIcons" 
          :key="`${iconName}-${index}`" 
          :src="getIconPath(iconName)" 
          alt=""
          :data-icon-name="iconName"
        >
      </div>
      <div v-if="isCurrent" class="opc-plan-card__current-badge">{{ $t('billing.common.billing_cycle_current') }}</div>
      <span class="opc-plan-card__plan-name">{{ plan.name }}</span>
      <SyncHeight group-id="plan-card:plan-title">
        <div class="plan-title">
          <h3 class="plan-title__main">{{ plan.title }}</h3>
          <p class="plan-title__description" v-if="plan.description">{{ plan.description }}</p>
        </div>
      </SyncHeight>
      <SyncHeight group-id="plan-card:plan-price">
        <div class="plan-price">
          <template v-if="plan.priceString">
            <p class="plan-price__main">{{ plan.priceString }}</p>
          </template>
          <template v-else-if="priceAmount">
            <p class="plan-price__main">{{ formatedPrice }}</p>
            <p class="plan-price__desc">{{ formatedPriceDesc }}</p>
          </template>
        </div>
      </SyncHeight>
      <SyncHeight group-id="plan-card:plan-btn">
        <zm-button
          v-if="!plan.hideBtn"
          size="lg"
          keep-text
          :loading="isLoading"
          :type="isPopular ? 'primary' : 'secondary'"
          :class="[{ 'plan-btn__common' : !isPopular }, 'plan-btn']"
          :aria-label="`${$t('billing.trial.buy_now')} ${plan.name}`"
          @click="handleBuyNow"
          data-testid="plan-card-buy-now"
        >
          {{ plan.btnCtaLabel || $t('billing.trial.buy_now') }}
        </zm-button>
      </SyncHeight>
      <div v-if="plan.highlightsText" class="plan-highlights">
        <div class="plan-highlights__title">{{ plan.highlightsText }}</div>
        <SyncHeight group-id="plan-card:plan-highlights" :disabled="!isHighlightsCollapsed">
          <div class="plan-highlights__body">
            <div v-for="(item) in filteredHighlights" class="plan-highlights__feature" :key="item.category">
              <template v-if="item.type === 'divide'">
                <hr style="width: 100%; margin: 16px 0;" />
              </template>
              <template v-else>
              <img :src="getIconPath(item.categoryIconName)" :data-icon-name="item.categoryIconName" alt="">
              <p class="plan-highlights__feature__category">{{ item.category }}</p>
              <div class="plan-highlights__list" v-for="(feature, index) in getVisibleFeatures(item)" :key="index">
                <template v-if="!feature.hideCheckIcon">
                  <img v-if="isDarkTheme" class="plan-highlights__list__check" src="@/assets/plan-card/check-dark-theme.svg" alt="">
                  <img v-else class="plan-highlights__list__check" src="@/assets/plan-card/check.svg" alt="">
                </template>
                <div>
                  <span
                    v-if="feature.title"
                    class="plan-highlights__list__title">{{ feature.title }}</span>
                  <span
                    v-html="feature.text"
                    class="plan-highlights__list__text"
                    :class="{'plan-highlights__list__bolder-text': feature.bold}">
                  </span>
                  <span v-if="feature.suffix" style="margin-left: 4px"> {{ feature.suffix }}</span>
                  <p
                    v-if="feature.link"
                    role="button"
                    tabindex="0"
                    class="plan-highlights__list__link-text"
                    @click="handleClick(feature.link)"
                    @keydown.enter.prevent="handleClick(feature.link)"
                    @keydown.space.prevent="handleClick(feature.link)"
                  >
                    {{ feature.link.text }}
                  </p>
                </div>
              </div>
              <p v-if="item.footnote" class="plan-highlights__footnote">{{ item.footnote }}</p>
              </template>
            </div>
          </div>
        </SyncHeight>
        <zm-button
          v-if="hasCollapsedFeature"
          class="plan-highlights__toggle"
          type="button"
          :icon="isHighlightsCollapsed ? ChevronRightSmall : ChevronUpSmall"
          @click="toggleHighlights"
        >
          {{ isHighlightsCollapsed ? $t('planCard.seeMoreHighlights') : $t('planCard.seeLessHighlights') }}
        </zm-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ChevronRightSmall, ChevronUpSmall } from '@zoom/icons-vue';

import { THEME } from '@/views/checkout/constant';
import SyncHeight from '@/components/sync-height.vue';

import discountedMonthPrice from '@/common/mixin/discounted-month-price';
import { BILLING_CYCLE } from '@/common/constants';
import { getPerMonthPlanUnit } from '@/common/product-unit';

export default {
  name: 'PlanCard',
  mixins: [ discountedMonthPrice ],
  components: {
    SyncHeight,
  },
  props: {
    plan: {
      type: Object,
      required: true
    },
    isCurrent: {
      type: Boolean,
      default: false
    },
    isPopular: {
      type: Boolean,
      default: false
    },
    loadingId: {
      type: String,
      default: ''
    },
    isMiddle: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isHighlightsCollapsed: true,
    }
  },
  setup() {
    return {
      ChevronRightSmall,
      ChevronUpSmall,
    }
  },
  computed: {
    ...mapGetters(['groupByPlanCodePlanMap', 'clientWebViewTheme']),
    isDarkTheme() {
      return this.clientWebViewTheme === THEME.DARK
    },
    isLoading() {
      return this.loadingId === this.plan.id;
    },
    priceAmount() {
      if (!this.plan.priceKey) {
        return 0;
      }

      const groupByPlanCodePlanMap = this.groupByPlanCodePlanMap || {};
      const priceKeys = Array.isArray(this.plan.priceKey) ? this.plan.priceKey : [this.plan.priceKey];

      const matchedPlan = priceKeys
        .map((key) => groupByPlanCodePlanMap[key])
        .find(Boolean);

      return matchedPlan?.amount ?? 0;
    },
    // get yearly price
    priceByMonth() {
      return this.getPriceOfMonth(this.priceAmount, BILLING_CYCLE.ANNUAL, {})
    },
    formatedPrice() {
      const { currency = {}, integer, decimal = '' } = this.priceByMonth
      const formatedDecimal = decimal ? `.${decimal}` : ''
      if (currency.place === 'begin') {
        return `${currency.symbol}${integer}${formatedDecimal}`
      } else {
        return `${integer}${formatedDecimal}${currency.symbol}`
      }
    },
    formatedPriceDesc() {
      const billY = this.$t('billing.bill_annually')
      return `${this.formatedUnit} · ${billY}`
    },
    formatedUnit() {
      return getPerMonthPlanUnit(this.plan.priceUnit)
    },
    hasCollapsedFeature() {
      return (this.plan.highlights || []).some((item) => {
        const { collapsedCount } = item || {};
        return Number.isFinite(collapsedCount) && collapsedCount >= 0;
      });
    },
    planIcons() {
      const iconNames = this.plan?.iconName;
      if (!iconNames) {
        return [];
      }
      return Array.isArray(iconNames) ? iconNames.filter(Boolean) : [iconNames];
    },
    filteredHighlights() {
      const highlights = this.plan.highlights || [];
      if (!this.isHighlightsCollapsed) {
        return highlights;
      }
      const result = [];
      let stop = false;
      highlights.forEach((item) => {
        if (stop) {
          return;
        }
        if (item?.type === 'divide') {
          result.push(item);
          return;
        }
        const { collapsedCount } = item || {};
        if (Number.isFinite(collapsedCount) && collapsedCount >= 0) {
          result.push(item);
          stop = true;
          return;
        }
        result.push(item);
      });
      return result;
    }
  },
  methods: {
    getIconPath(iconName) {
      try {
        return require(`@/assets/plan-card/${iconName}.svg`)
      } catch (e) {
        console.warn(`Icon not found: ${iconName}`)
        return ''
      }
    },
    handleClick(linkConfig) {
      const { event, callback } = linkConfig;
      if (callback) {
        callback();
        return;
      }
      this.$context.$emit('plan-card-event', { event })
    },
    handleBuyNow() {
      this.$emit('buyNow', this.plan.id)
    },
    toggleHighlights() {
      this.$emit('toggleHighlights', this.isHighlightsCollapsed ? 'expand' : 'collapse');
      this.isHighlightsCollapsed = !this.isHighlightsCollapsed;
    },
    getVisibleFeatures(item = {}) {
      const { collapsedCount, features = [] } = item;
      if (!Number.isFinite(collapsedCount) || collapsedCount < 0) {
        return features;
      }
      if (this.isHighlightsCollapsed) {
        return features.slice(0, collapsedCount);
      }
      return features;
    }
  },
  mounted() {
  }
}
</script>

<style lang="scss" scoped>

@import '@/styles/scss-utils.scss';
@import "@/styles/variables.scss";

%bolder-text {
  color: $zoom-color-text-stronger-neutral;
  font-size: 32px;
  font-weight: 600;
}
%desc-text {
  color: $zoom-color-text-stronger-neutral;
  font-size: 14px;
  font-weight: 400;
}

%title-text {
  color: $zoom-color-text-stronger-neutral;
  font-size: 14px;
  font-weight: 600;
}

.opc-plan-popular-label {
  width: 100%;
  left: 0;
  display: flex;
  padding: 6px 0 5px 0;
  justify-content: center;
  align-items: center;
  border-radius: 16px 16px 0 0;
  background: $zoon-card-head-link-color;
  color: $background-color;
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  position: absolute;
  top: -26px;
}

.opc-plan-card {
  padding: 16px;
  border: 1px solid $color-border-subtle-neutral;
  border-radius: 16px;
  height: 100%;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  display: grid;
  align-content: flex-start;
  grid-auto-rows: auto;
  &__icon {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    img {
      width: 32px;
      height: 32px;
    }
  }

  &__current-badge {
    color: #2057b1;
    background-color: #f2f8ff;
    border: 1px solid #a8ccf8;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    padding: 0 6px;
    line-height: 18px;
    height: 20px;
    margin-left: 6px;
    display: inline-block;
    position: absolute;
    right: 16px;
    top: 22px;
  }

  &__plan-name {
    @extend %title-text;
    margin-top: 8px;
  }

  .plan-title {
    margin-top: 16px;
    &__main {
      @extend %bolder-text;
      margin: 0;
    }
    &__description {
      @extend %desc-text
    }
  }

  .plan-price {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    &__main {
      @extend %bolder-text
    }
    &__desc {
      @extend %desc-text;
      margin: 0;
    }
  }

  .plan-btn {
    margin-top: 24px;
    &.zbo-button {
      width: 100%;
    }
    &.plan-btn__common {
      border-radius: 12px;
      border: 1px solid $text-inactive2;
      background: $background-color;
    }
  }

  .plan-highlights {
    margin-top: 32px;
    display: flex;
    flex-direction: column;
    &__footnote {
      margin-left: 28px;
      @extend %desc-text;
    }

    &__toggle {
      margin-top: 12px;
      padding: 0;
      border: none;
      background: none;
      cursor: pointer;
      display: inline-flex;
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
      color: $zoom-color-text-stronger-neutral;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
    }

    &__title {
      @extend %title-text;
      display: flex;
      min-height: 32px;
      padding: 12px 0;
      align-items: center;
      gap: 8px;
      align-self: stretch;
    }
    &__body {
      flex: 1;
    }
    &__feature {
      display: flex;
      flex-wrap: wrap;
      column-gap: 8px;
      padding: 8px 0;

      &__category {
        font-size: 14px;
        line-height: 18px;
        margin-bottom: 8px;
        @extend %title-text;
        flex: 1;
      }
      img {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-top: 1px;
      }
    }
    &__list {
      flex-basis: 100%;
      display: flex;
      gap: 8px;
      margin-left: 28px;
      margin-bottom: 8px;
      @extend %desc-text;

      &:last-child {
        margin-bottom: 0;
      }

      &__title {
        display: block;
        color: $zoom-color-text-stronger-neutral;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        margin-bottom: 4px;
      }

      &__text {
        :deep a {
          color: $zoon-card-head-link-color;
          cursor: pointer;
        }
        :deep b {
          font-weight: 600;
        }
      }

      &__bolder-text {
        font-weight: 700;
      }

      &__link-text {
        line-height: 18px;
        cursor: pointer;
        @extend %desc-text;
        color: $zoon-card-head-link-color;
      }

      span {
        line-height: 18px;
        margin-bottom: 4px;
      }
    }
  }
}

.opc-card-is-popular {
  border: 1px solid $zoon-card-head-link-color;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.opc-card-is-common {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;
}

.opc-card-is-middle {
  border-radius: 0;
  border-right: none;
}

</style>




